import { ratingStrings } from '../../content/ratings';
import { musicStrings } from '../../content/music';

/**
 * Normalizes the requested operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const clean = (value) => String(value ?? '').trim();
/**
 * Performs the metric operation.
 *
 * @param {*} label - label value.
 * @param {*} value - Value to inspect or transform.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const metric = (label, value) => clean(value) ? { label, value: clean(value) } : null;
/**
 * Performs the minutes operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const minutes = (value) => Number(value) > 0 ? `${Number(value)} min` : '';
/** Keeps the numeric score available to both the share preview and image renderer. */
const ratingMetric = (value) => {
  const rating = Number(value);
  return clean(value) && rating > 0 && rating <= 5
    ? { label: 'Rating', value: ratingStrings.value(rating), rating }
    : null;
};
/**
 * Performs the first metric operation.
 *
 * @param {Array<*>} values - Values to inspect or transform.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const firstMetric = (...values) => values.find(Boolean) || null;
/**
 * Performs the times made operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
const timesMade = (value) => {
  const count = Number(value);
  if (count <= 0) return '';
  return count === 1 ? 'Once' : `${count} times`;
};

/**
 * Performs the display date operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
const displayDate = (value) => {
  const normalized = clean(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return clean(value);
  const [year, month, day] = normalized.split('-').map(Number);
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    .format(new Date(year, month - 1, day));
};

/**
 * Performs the trip days operation.
 *
 * @param {*} start - start value.
 * @param {*} end - end value.
 * @returns {*} The derived operation result.
 */
const tripDays = (start, end) => {
  const startValue = clean(start).slice(0, 10);
  const endValue = clean(end).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startValue) || !/^\d{4}-\d{2}-\d{2}$/.test(endValue)) return '';
  const startDate = new Date(`${startValue}T00:00:00Z`);
  const endDate = new Date(`${endValue}T00:00:00Z`);
  const count = Math.floor((endDate - startDate) / 86400000) + 1;
  return count > 0 ? `${count}` : '';
};

/**
 * Performs the date range operation.
 *
 * @param {*} start - start value.
 * @param {*} end - end value.
 * @returns {*} The derived operation result.
 */
function dateRange(start, end) {
  if (!clean(start)) return displayDate(end);
  if (!clean(end) || start === end) return displayDate(start);
  return `${displayDate(start)} – ${displayDate(end)}`;
}

/**
 * Creates share highlight.
 *
 * @param {*} hobby - hobby value.
 * @param {*} entry - entry value.
 * @param {string} variant - variant value.
 * @returns {*} The derived operation result.
 */
export function buildShareHighlight(hobby, entry, variant = 'entry') {
  const base = {
    id: `${hobby}:${entry?.id || entry?.title || entry?.name || 'highlight'}`,
    hobby,
    title: clean(entry?.title || entry?.name) || 'My Hobtalk highlight',
    kind: 'moment',
    headline: 'A hobby moment',
    subtitle: '',
    subtitleWithoutDate: undefined,
    mediaUrl: '',
    mediaPresentation: 'fallback',
    metrics: [],
    review: '',
    heroMetric: null,
  };

  switch (hobby) {
    case 'cooking':
      const cookingMetrics = [
        metric('Total time', entry.totalTime),
        metric('Servings', entry.servings),
        ratingMetric(entry.rating),
        metric('Made', timesMade(entry.cookingSummary?.timesCooked)),
      ].filter(Boolean).slice(0, 4);
      return {
        ...base,
        headline: entry.favorite ? 'A favorite recipe' : Number(entry.cookingSummary?.timesCooked) > 0 ? 'Made this recipe' : 'A recipe to try',
        subtitle: clean(entry.cuisine),
        mediaUrl: clean(entry.coverPhotoUrl),
        mediaPresentation: clean(entry.coverPhotoUrl) ? 'landscape' : 'fallback',
        metrics: cookingMetrics,
        heroMetric: firstMetric(cookingMetrics.find((item) => item.label === 'Rating'), cookingMetrics.find((item) => item.label === 'Total time'), cookingMetrics.find((item) => item.label === 'Servings')),
      };
    case 'traveling':
      const travelMetrics = [
        metric('Dates', dateRange(entry.startDate, entry.endDate)),
        metric('Days', tripDays(entry.startDate, entry.endDate)),
        metric('Type', entry.tripType),
        metric('Status', entry.status),
      ].filter(Boolean).slice(0, 4);
      return {
        ...base,
        headline: clean(entry.status).toLowerCase() === 'completed' ? 'Trip complete' : 'A trip to remember',
        subtitle: clean(entry.destination || 'Trip highlight'),
        // Only user-owned uploads are embedded in exported artifacts. Provider
        // images remain inside Hobtalk until their redistribution rights are explicit.
        mediaUrl: clean(entry.coverPhotoUrl),
        mediaPresentation: clean(entry.coverPhotoUrl) ? 'landscape' : 'fallback',
        metrics: travelMetrics,
        heroMetric: firstMetric(travelMetrics.find((item) => item.label === 'Days'), travelMetrics[0]),
      };
    case 'hiking':
      const hikingMetrics = [
        metric('Distance', entry.distance ? `${entry.distance} ${entry.distanceUnit || ''}`.trim() : ''),
        metric('Elevation', Number(entry.elevationGain) > 0 ? `${entry.elevationGain} ${entry.elevationUnit || ''}`.trim() : ''),
        metric('Duration', minutes(entry.actualDurationMinutes || entry.durationMinutes)),
        ratingMetric(entry.rating),
      ].filter(Boolean).slice(0, 4);
      return {
        ...base,
        headline: clean(entry.status).toLowerCase() === 'completed' ? 'Trail complete' : 'Trail highlight',
        subtitle: clean(entry.location || 'Trail highlight'),
        mediaUrl: clean(entry.coverPhotoUrl),
        mediaPresentation: clean(entry.coverPhotoUrl) ? 'landscape' : 'fallback',
        metrics: hikingMetrics,
        heroMetric: firstMetric(hikingMetrics.find((item) => item.label === 'Distance'), hikingMetrics[0]),
        review: clean(entry.review),
      };
    case 'fitness':
      if (variant === 'routine') {
        const routineMetrics = [
          metric('Schedule', Number(entry.daysPerWeek) > 0 ? `${entry.daysPerWeek} days/week` : ''),
          metric('Workouts', entry.days?.length ? `${entry.days.length}` : ''),
          metric('Status', entry.status),
        ].filter(Boolean);
        return {
          ...base,
          headline: 'My workout routine',
          subtitle: clean(entry.goal || 'Workout routine'),
          metrics: routineMetrics,
          heroMetric: routineMetrics[0] || null,
        };
      }
      const fitnessSource = clean(entry.source).toLowerCase() === 'apple_health' ? 'Apple Health' : '';
      const workoutMetrics = [
        metric('Duration', minutes(entry.durationMinutes)),
        metric('Exercises', entry.strengthExercises?.length ? `${entry.strengthExercises.length}` : ''),
        metric('Sets', (() => {
          const count = entry.strengthExercises?.reduce((total, exercise) => total + (exercise.sets?.length || 0), 0) || 0;
          return count > 0 ? `${count}` : '';
        })()),
        metric(fitnessSource ? 'Source' : 'Distance', fitnessSource || (entry.distance ? `${entry.distance} ${entry.distanceUnit || ''}`.trim() : '')),
      ].filter(Boolean).slice(0, 4);
      return {
        ...base,
        headline: clean(entry.status).toLowerCase() === 'completed' ? 'Workout complete' : 'Workout highlight',
        subtitle: [entry.activityType || entry.workoutType, displayDate(entry.completedAt || entry.workoutDate)].map(clean).filter(Boolean).join(' · '),
        subtitleWithoutDate: clean(entry.activityType || entry.workoutType),
        metrics: workoutMetrics,
        heroMetric: firstMetric(workoutMetrics.find((item) => item.label === 'Duration'), workoutMetrics[0]),
        review: clean(entry.review),
      };
    case 'movies':
      const movieMetrics = [metric('Status', entry.status), ratingMetric(entry.rating), metric('Genre', entry.genres?.[0])].filter(Boolean);
      return {
        ...base,
        headline: clean(entry.status).toLowerCase() === 'watched' ? 'Just watched' : 'On my watchlist',
        subtitle: [entry.releaseYear, entry.director].map(clean).filter(Boolean).join(' · '),
        metrics: movieMetrics,
        heroMetric: movieMetrics.find((item) => item.label === 'Rating') || null,
        review: entry.spoiler ? '' : clean(entry.review),
      };
    case 'tvshows':
      const tvMetrics = [metric('Status', entry.userStatus || entry.status), ratingMetric(entry.rating), metric('Genre', entry.genres?.[0])].filter(Boolean);
      return {
        ...base,
        headline: ['completed', 'watched'].includes(clean(entry.userStatus || entry.status).toLowerCase()) ? 'Series complete' : 'Currently watching',
        subtitle: [entry.releaseYear || entry.firstAirYear, entry.creator].map(clean).filter(Boolean).join(' · '),
        metrics: tvMetrics,
        heroMetric: tvMetrics.find((item) => item.label === 'Rating') || null,
        review: entry.spoiler ? '' : clean(entry.review),
      };
    case 'reading':
      const readingMetrics = [
        metric('Status', entry.shelf),
        metric('Progress', Number(entry.currentPercent) > 0 ? `${Math.round(entry.currentPercent)}%` : ''),
        ratingMetric(entry.rating),
      ].filter(Boolean);
      return {
        ...base,
        headline: clean(entry.shelf).toLowerCase() === 'read' ? 'Finished reading' : 'Currently reading',
        subtitle: [entry.authors?.join(', '), entry.publishedYear].map(clean).filter(Boolean).join(' · '),
        metrics: readingMetrics,
        heroMetric: firstMetric(readingMetrics.find((item) => item.label === 'Progress'), readingMetrics.find((item) => item.label === 'Rating')),
        review: entry.spoiler ? '' : clean(entry.review),
      };
    case 'dining':
      const isVisit = variant === 'visit';
      const diningMetrics = (isVisit ? [
        metric('Visited', displayDate(entry.visitedOn)),
        metric('Tried', entry.itemsTried?.length ? `${entry.itemsTried.length}` : ''),
        ratingMetric(entry.rating),
      ] : [
        metric('Status', entry.status),
        metric('Cuisine', entry.cuisine),
        ratingMetric(entry.rating),
      ]).filter(Boolean);
      return {
        ...base,
        headline: isVisit ? 'A dining visit' : entry.favorite ? 'A favorite spot' : clean(entry.status).toLowerCase() === 'visited' ? 'A dining spot' : 'A place to try',
        subtitle: clean(entry.city || entry.kind),
        mediaUrl: clean(entry.coverPhotoUrl),
        mediaPresentation: clean(entry.coverPhotoUrl) ? 'landscape' : 'fallback',
        metrics: diningMetrics,
        heroMetric: firstMetric(diningMetrics.find((item) => item.label === 'Rating'), diningMetrics[0]),
        review: clean(entry.notes),
      };
    case 'creative':
      const creativeMetrics = [
        metric('Status', entry.status),
        metric('Progress', entry.steps?.length ? `${entry.steps.filter((step) => step.completed).length} of ${entry.steps.length} steps` : ''),
        ratingMetric(entry.rating),
      ].filter(Boolean);
      return {
        ...base,
        headline: clean(entry.status).toLowerCase() === 'completed' ? 'Project complete' : 'A creative project',
        subtitle: clean(entry.category === 'Other' ? entry.customCategory : entry.category),
        mediaUrl: clean(entry.coverPhotoUrl),
        mediaPresentation: clean(entry.coverPhotoUrl) ? 'landscape' : 'fallback',
        metrics: creativeMetrics,
        heroMetric: firstMetric(creativeMetrics.find((item) => item.label === 'Progress'), creativeMetrics[0]),
        review: clean(entry.review),
      };
    case 'music':
      const musicMetrics = [
        metric('Date', displayDate(entry.happenedOn)),
        metric('Instrument', entry.instrument),
        metric('Time', minutes(entry.durationMinutes)),
      ].filter(Boolean);
      return {
        ...base,
        headline: 'A music moment',
        subtitle: clean(entry.activities?.map((activity) => musicStrings.activityLabels[activity] || activity).join(' · ')),
        metrics: musicMetrics,
        heroMetric: firstMetric(musicMetrics.find((item) => item.label === 'Time'), musicMetrics[0]),
        review: clean(entry.notes),
      };
    default:
      return base;
  }
}

/**
 * Performs share highlight.
 *
 * @param {*} highlight - highlight value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
export function openShareHighlight(highlight) {
  window.dispatchEvent(new CustomEvent('hobtalk:share-highlight', { detail: highlight }));
}
